import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { anneAudits, anneResolveFault, qcGates, reseatAgent, kalshiDrill, invoFeed, invoFeedSet } from "../lib/api";

const ST = { active: "text-emerald-300", probation: "text-amber-300", benched: "text-rose-300", fired: "text-rose-400" };
const when = (t) => (t ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const Section = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-fuchsia-300/15 bg-[#0a0614] p-4 space-y-2">
    <h3 className="text-[11px] font-bold tracking-[0.2em] text-fuchsia-200/80">{title}</h3>
    {children}
  </div>
);

const AuditRow = ({ a }) => (
  <div data-testid={`anne-audit-${a.id}`} className="rounded-lg border border-white/5 bg-black/30 p-3 space-y-1.5">
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.status === "closed" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{a.status.toUpperCase()}</span>
      <span className="text-fuchsia-200 font-bold">{a.responsible}</span>
      <span className="text-slate-400">{a.scope === "calls" ? "missed call" : a.event?.symbol} · {when(a.created_at)}</span>
      {a.repeat_count > 0 && <span data-testid="anne-repeat-count" className="text-rose-300 font-bold">repeat x{a.repeat_count + 1}</span>}
      {a.event?.pnl_usd != null && <span className="ml-auto font-mono text-rose-300">${Number(a.event.pnl_usd).toFixed(2)}</span>}
    </div>
    <p className="text-xs text-slate-300"><b className="text-slate-400">What:</b> {a.what_happened}</p>
    <p className="text-xs text-slate-300"><b className="text-slate-400">Root cause:</b> {a.root_cause}</p>
    <p className="text-xs text-fuchsia-100"><b className="text-fuchsia-300">Character change:</b> {a.character_rule}</p>
    <p className="text-xs text-cyan-100"><b className="text-cyan-300">Programming change:</b> [{a.programming_rule?.kind}] {a.programming_rule?.text}</p>
  </div>
);

export default function NexusMissAnne() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [qc, setQc] = useState(null);
  const load = useCallback(() => {
    anneAudits().then(setD).catch(() => toast.error("Could not load Miss Anne"));
    qcGates().then(setQc).catch(() => {});
    invoFeed().then(setInvo).catch(() => {});
  }, []);
  const [invo, setInvo] = useState(null);
  const reseat = async (agent) => {
    setBusy(agent);
    try { const r = await reseatAgent(agent); r.ok ? toast.success(`${agent} re-seated — strikes reset`) : toast.error(r.error); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not re-seat"); }
    finally { setBusy(""); }
  };
  useEffect(() => { load(); }, [load]);
  const resolve = async (id) => {
    setBusy(id);
    try { await anneResolveFault(id); toast.success("Fix marked resolved — engine back to full size"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resolve"); }
    finally { setBusy(""); }
  };
  if (!d) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading Miss Anne…</div>;
  const acc = d.accountability || {};
  return (
    <div data-testid="miss-anne-tab" className="p-4 md:p-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-fuchsia-300" />
        <div>
          <h2 className="text-2xl font-bold text-fuchsia-100">Miss Anne</h2>
          <p className="text-xs text-slate-400">Quality Control. Reports only to Sohel. The house, the desks and Nexus report to her.</p>
        </div>
        <button data-testid="anne-refresh-btn" onClick={load} className="ml-auto p-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors"><RefreshCw className="w-4 h-4 text-slate-300" /></button>
      </div>
      {acc.house_halt && <p data-testid="anne-house-halt" className="text-sm text-rose-300 font-bold">HOUSE HALT today — day loss ${Math.abs(acc.house_halt.loss).toFixed(2)}. New entries stopped until tomorrow.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="AGENT STRIKES (7 DAYS)" testid="anne-strikes">
          {(acc.agents || []).length === 0 && <p className="text-xs text-slate-500">No agents graded yet.</p>}
          {(acc.agents || []).map((a) => (
            <div key={a.agent} data-testid={`anne-agent-${a.agent}`} className="flex items-center text-xs font-mono">
              <span className="text-slate-200 w-32">{a.agent}</span>
              <span className="text-slate-400 w-24">{a.strikes_7d} strikes</span>
              <span className={`font-bold ${ST[a.status] || ""}`}>{a.status.toUpperCase()}</span>
            </div>))}
        </Section>
        <Section title="QC GATES · BENCH (SOHEL RE-SEATS ONLY) · ESCALATIONS · LOCKED CALLS" testid="anne-qc-gates">
          {(qc?.benched || []).length === 0 && <p className="text-xs text-slate-500">Nobody benched.</p>}
          {(qc?.benched || []).map((b) => (
            <div key={b.name} data-testid={`anne-benched-${b.name}`} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-rose-300 w-32">{b.name}</span>
              <span className="text-slate-400 flex-1">{b.why} · until {when(b.benched_until)}</span>
              <button data-testid={`anne-reseat-${b.name}`} disabled={busy === b.name} onClick={() => reseat(b.name)}
                className="px-2 py-1 rounded border border-emerald-300/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors">Re-seat</button>
            </div>))}
          {invo && (
            <div className="text-xs pt-1 space-y-0.5" data-testid="anne-invo-feed">
              <div className="flex items-center gap-2">
                <span className={`font-bold ${invo.fresh ? "text-emerald-300" : "text-rose-300"}`} data-testid="anne-invo-health">INVO FEED {invo.fresh ? "LIVE" : "DOWN"}</span>
                <span className="text-slate-400 flex-1">top-{invo.leaders} leaderboard · {invo.open_moves} open moves · last ok {when(invo.last_ok)}{invo.fails ? ` · ${invo.fails} failed pulls: ${invo.last_error}` : ""}</span>
                <button data-testid="anne-invo-toggle" onClick={async () => { await invoFeedSet(!invo.enabled); load(); }}
                  className={`px-2 py-1 rounded border transition-colors ${invo.enabled ? "border-emerald-300/30 text-emerald-300" : "border-rose-300/40 text-rose-300"}`}>{invo.enabled ? "ON · kill" : "OFF · enable"}</button>
              </div>
              <p className="text-slate-400" data-testid="anne-invo-score">Invo-tagged trades {invo.scorecard?.days}d: {invo.scorecard?.invo?.closed} closed, win {invo.scorecard?.invo?.win_pct ?? "—"}%, ${invo.scorecard?.invo?.net_usd} · house win {invo.scorecard?.house?.win_pct ?? "—"}%{invo.cut_reason ? ` · CUT: ${invo.cut_reason}` : ""}</p>
            </div>)}
          <div className="flex items-center gap-2 text-xs pt-1" data-testid="anne-kalshi-drill">
            <span className="text-violet-200 flex-1">KALSHI THESIS DRILL: {qc?.kalshi_drill ? `${qc.kalshi_drill.passed}/${qc.kalshi_drill.needed} passed · ${when(qc.kalshi_drill.at)}${qc.kalshi_drill.ready ? " · READY TO RE-SEAT" : ""}` : "not run yet"}
              {(qc?.kalshi_blocks || []).length > 0 && ` · ${qc.kalshi_blocks.length} Kalshi buys blocked (no thesis) 7d`}</span>
            <button data-testid="anne-kalshi-drill-run" disabled={busy === "drill"} onClick={async () => { setBusy("drill"); try { await kalshiDrill(); toast.success("Drill started — Anne posts the audit in the Anne chat in a few minutes"); } catch { toast.error("Could not start drill"); } finally { setBusy(""); } }}
              className="px-2 py-1 rounded border border-violet-300/30 text-violet-200 hover:bg-violet-500/10 transition-colors">Run drill</button>
          </div>
          {(qc?.kalshi_drill?.examples || []).map((x) => (
            <p key={x.ticker} data-testid={`anne-drill-${x.ticker}`} className={`text-[11px] ${x.gate_ok && x.pass ? "text-emerald-300" : "text-rose-300"}`}>
              {x.gate_ok && x.pass ? "PASS" : "FAIL"} {x.ticker} {x.side?.toUpperCase()} · {x.note}</p>))}
          {(qc?.escalations || []).slice(0, 6).map((e) => (
            <p key={e.id} data-testid={`anne-escalation-${e.id}`} className="text-xs text-amber-200"><b>G1 ESCALATION</b> {e.ticker} {Number(e.move_pct).toFixed(1)}% · {e.reason} · {when(e.at)}</p>))}
          {(qc?.locked_drops || []).slice(0, 6).map((l) => (
            <p key={l.id} data-testid={`anne-lock-${l.id}`} className="text-xs text-cyan-200"><b>G3 LOCK</b> {l.ticker} Crams {l.crams_size} not filled · signed {l.agent}: {l.why} · {when(l.at)}</p>))}
        </Section>
        <Section title="SYSTEM FAULTS (ENGINE AT 50% UNTIL RESOLVED)" testid="anne-faults">
          {(acc.faults || []).length === 0 && <p className="text-xs text-slate-500">No system faults.</p>}
          {(acc.faults || []).map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-amber-200">{f.module}</span>
              <span className="text-slate-500">x{f.count} · {f.last_reason}</span>
              {f.status === "open"
                ? <button data-testid={`anne-resolve-${f.id}`} disabled={busy === f.id} onClick={() => resolve(f.id)} className="ml-auto px-2 py-1 rounded-md border border-emerald-400/40 text-emerald-300 text-[11px] font-bold hover:bg-emerald-400/10 transition-colors">{busy === f.id ? "…" : "Mark fix resolved"}</button>
                : <span className="ml-auto text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> resolved</span>}
            </div>))}
        </Section>
      </div>
      <Section title={`AUDITS (${(d.audits || []).length})`} testid="anne-audits">
        {(d.audits || []).length === 0 && <p className="text-xs text-slate-500">No audits yet.</p>}
        <div className="space-y-2">{(d.audits || []).map((a) => <AuditRow key={a.id} a={a} />)}</div>
      </Section>
      <Section title="HIRE / FIRE LOG" testid="anne-hire-fire-log">
        {(d.log || []).filter((l) => ["hired", "fired", "benched", "fault-resolved", "backfill", "weekly"].includes(l.kind)).map((l) => (
          <p key={l.id} className="text-xs text-slate-300"><span className="text-slate-500 font-mono">{when(l.at)}</span> {l.msg}</p>))}
        {!(d.log || []).length && <p className="text-xs text-slate-500">No hires or fires yet.</p>}
      </Section>
    </div>
  );
}
